import { useEffect, useState, ReactNode } from 'react'

import Bookkeeping from '@scouts/bookkeeping'

import { PATH_NEW_ONBOARDING_INVOICING } from '@/constants'
import { getIsCompanyVariant } from '@/domain/variant'
import { useProfile } from '@/store/profile'

import { useBookkeepingData } from '@/hooks/use-bookkeeping-data'
import { usePostHogFeatureFlags } from '@/hooks/use-posthog-feature-flags'

import { BookkeepingConfigurationContext, DEFAULT_CONTEXT } from './BookkeepingConfigurationContext'
import { useFilters } from './use-filters'

interface BookkeepingConfigurationProviderProps {
    children: ReactNode
}

export const BookkeepingConfigurationProvider = ({ children }: BookkeepingConfigurationProviderProps) => {
    const contextData = useState(DEFAULT_CONTEXT)
    const [config, setConfig] = contextData
    const data = useBookkeepingData()
    const { isBookkeepingOptedIn: isBookkeepingDoneByAccountant, subscriptionStatus, isCompany, variant } = useProfile()
    const { filters: availableDateRangeFilterShortcuts } = useFilters()

    const isCompanyOrCompanyVariant = isCompany || getIsCompanyVariant(variant)

    const shouldDisplayBookkeepingOnboarding = !subscriptionStatus && !isCompanyOrCompanyVariant // disable onboarding for company customers

    const { featureInvoicePricesIncludeVAT } = usePostHogFeatureFlags()

    useEffect(() => {
        setConfig((currentConfig) => ({
            ...currentConfig,
            featureFlags: { ...currentConfig.featureFlags, featureInvoicePricesIncludeVAT },
            isBookkeepingDoneByAccountant,
            shouldDisplayBookkeepingOnboarding,
            availableDateRangeFilterShortcuts,
            isBankingDisabled: isCompanyOrCompanyVariant,
            invoicingOnboardingPath: isCompanyOrCompanyVariant ? undefined : PATH_NEW_ONBOARDING_INVOICING, // disable onboarding for company customers
            isCompany: isCompanyOrCompanyVariant,
        }))
    }, [
        featureInvoicePricesIncludeVAT,
        shouldDisplayBookkeepingOnboarding,
        isBookkeepingDoneByAccountant,
        isCompanyOrCompanyVariant,
        availableDateRangeFilterShortcuts,
        setConfig,
    ])

    return (
        <BookkeepingConfigurationContext.Provider value={contextData}>
            <Bookkeeping.StoreProvider config={config} data={data}>
                {children}
            </Bookkeeping.StoreProvider>
        </BookkeepingConfigurationContext.Provider>
    )
}
